import { createContext, useContext } from "react";

import type {
  RecipeVariantProps,
  SlotRecipeRuntimeFn,
  SlotRecipeVariantRecord,
} from "@mobsuccess-devops/styled-system/types";

import type { z } from "zod";

type CreateSvaContextResult<
  Extra,
  S extends string,
  T extends SlotRecipeVariantRecord<S>,
> = {
  styles: SlotRecipeRuntimeFn<S, T>;
  useStyles(): {
    meta?: RecipeVariantProps<SlotRecipeRuntimeFn<S, T>> & Extra;
    classes: ReturnType<SlotRecipeRuntimeFn<S, T>>;
  };
  StylesProvider: React.FC<{
    classes: ReturnType<SlotRecipeRuntimeFn<S, T>>;
    meta?: RecipeVariantProps<SlotRecipeRuntimeFn<S, T>> & Extra;
    children: React.ReactNode;
  }>;
};

export function createSvaContext<
  S extends string = string,
  T extends SlotRecipeVariantRecord<S> = SlotRecipeVariantRecord<S>,
  Schema extends z.ZodObject = z.ZodObject,
>(
  recipe: SlotRecipeRuntimeFn<S, T>,
  _extraSchema?: Schema,
): CreateSvaContextResult<z.infer<Schema>, S, T> {
  const Context = createContext<{
    meta?: RecipeVariantProps<typeof recipe> & z.infer<Schema>;
    classes: ReturnType<typeof recipe>;
  } | null>(null);
  return {
    styles: recipe,
    useStyles() {
      const styles = useContext(Context);
      if (!styles) {
        throw new Error("useStyles must be used within a StylesProvider");
      }
      return styles;
    },
    StylesProvider({ children, ...value }) {
      return <Context value={value}>{children}</Context>;
    },
  };
}
